import { httpClient } from '@/utils/httpClient';
import { buildPostgrestOrIlikeFilter } from '@/utils/postgrestFilters';
import { API_CONFIG } from '@/config/api';
import type {
  Entity,
  CreateEntityRequest,
  UpdateEntityRequest,
  CreateBulkEntityRequest,
} from '@/types/Entity';
import { logger } from '@equip/utils/logger';

const log = logger.child({ component: 'EntityService' });

export interface ListParams {
  type: string;
  limit?: number;
  offset?: number;
  order?: string;
  filters?: Record<string, string>;
}

export interface EntityId {
  type: string;
  id: string;
}

export class EntityService {
  private baseEndpoint = API_CONFIG.ENDPOINTS.ENTITIES;

  /**
   * List entities by type with pagination (TanStack Query pattern)
   */
  async list(params: ListParams): Promise<Entity[]> {
    const searchParams = new URLSearchParams({
      entity_type: `eq.${params.type}`,
      is_current: 'eq.true',
      order: params.order ?? 'display_name.asc',
    });

    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));

    if (params.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (value === undefined || value === null || value === '') continue;
        searchParams.set(key, value);
      }
    }

    const endpoint = `${this.baseEndpoint}?${searchParams}`;
    return await httpClient.get<Entity[]>(endpoint);
  }

  /**
   * Fetch every matching entity, paginating automatically to bypass db-max-rows.
   */
  async listAll(params: Omit<ListParams, 'limit' | 'offset'>): Promise<Entity[]> {
    const searchParams = new URLSearchParams({
      entity_type: `eq.${params.type}`,
      is_current: 'eq.true',
      order: params.order ?? 'display_name.asc',
    });

    if (params.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (value === undefined || value === null || value === '') continue;
        searchParams.set(key, value);
      }
    }

    const endpoint = `${this.baseEndpoint}?${searchParams}`;
    return await httpClient.getAll<Entity>(endpoint);
  }

  /**
   * Get single entity by type and ID (TanStack Query pattern)
   */
  async get({ type, id }: EntityId): Promise<Entity | null> {
    const searchParams = new URLSearchParams({
      id: `eq.${id}`,
      entity_type: `eq.${type}`,
      is_current: 'eq.true',
    });

    const endpoint = `${this.baseEndpoint}?${searchParams}`;
    const entities = await httpClient.get<Entity[]>(endpoint);
    return entities[0] ?? null;
  }

  /**
   * Create new entity (TanStack Query pattern)
   */
  async create(input: CreateEntityRequest): Promise<Entity> {
    // Use RPC to leverage idempotency-by-client_reference
    const rpcEndpoint = this.baseEndpoint.replace('/entities', '/rpc/create_entity');
    const payload = {
      p_entity_type: input.entity_type,
      p_entity_identifier: input.entity_identifier ?? null,
      p_display_name: input.display_name,
      p_entity_data: input.entity_data ?? {},
      p_metadata: input.metadata ?? {},
      p_status: input.status ?? 'active',
      p_client_reference: input.client_reference?.trim() || null,
    };
    const response = await httpClient.post<Entity | Entity[]>(rpcEndpoint, payload);
    if (Array.isArray(response)) {
      const entity = response[0];
      if (!entity) {
        throw new Error('Create entity RPC succeeded without returning a record.');
      }
      return entity;
    }
    if (!response) {
      throw new Error('Create entity RPC succeeded without returning a record.');
    }
    return response;
  }

  /**
   * Update entity (uses RPC for entity_data, PATCH for simple fields)
   */
  async update({
    type: _type,
    id,
    patch,
  }: {
    type: string;
    id: string;
    patch: UpdateEntityRequest;
  }): Promise<Entity> {
    // Always route updates through the RPC to avoid non-updatable view errors
    const rpcEndpoint = `${this.baseEndpoint.replace('/entities', '/rpc/update_entity_data')}`;
    const payload: Record<string, unknown> = {
      p_entity_id: id,
    };

    if (patch.entity_data !== undefined) {
      payload.p_entity_data = patch.entity_data;
    }
    if (patch.display_name !== undefined) {
      payload.p_display_name = patch.display_name;
    }
    if (patch.entity_identifier !== undefined) {
      payload.p_entity_identifier = patch.entity_identifier;
    }
    if (patch.status !== undefined) {
      payload.p_status = patch.status;
    }

    const response = await httpClient.post<Entity[] | Entity>(rpcEndpoint, payload);
    const updated = Array.isArray(response) ? response[0] : response;
    if (!updated) {
      throw new Error('Update request succeeded without returning updated entity data.');
    }
    return updated;
  }

  /**
   * Remove entity (soft delete - TanStack Query pattern)
   */
  async remove({ id }: { id: string }): Promise<void> {
    const rpcEndpoint = `${this.baseEndpoint.replace('/entities', '/rpc/update_entity_data')}`;
    await httpClient.post(rpcEndpoint, {
      p_entity_id: id,
      p_deleted_at: new Date().toISOString(),
    });
  }

  async getEntity(entityId: string, entityType?: string): Promise<Entity | null> {
    if (entityType) {
      return this.get({ type: entityType, id: entityId });
    }
    const searchParams = new URLSearchParams({
      id: `eq.${entityId}`,
      is_current: 'eq.true',
    });
    const endpoint = `${this.baseEndpoint}?${searchParams}`;
    const entities = await httpClient.get<Entity[]>(endpoint);
    return entities[0] ?? null;
  }

  async createEntity(data: CreateEntityRequest): Promise<Entity> {
    return this.create(data);
  }

  async updateEntity(entityId: string, data: UpdateEntityRequest): Promise<Entity> {
    // Determine entity type by fetching the record first
    const existing = await this.getEntity(entityId);
    if (!existing) throw new Error(`Entity not found: ${entityId}`);

    const mergedData = {
      ...data,
      entity_data:
        'entity_data' in data || 'status' in data
          ? {
              ...(existing.entity_data ?? {}),
              ...(data.entity_data ?? {}),
            }
          : existing.entity_data,
    };

    return this.update({ type: existing.entity_type, id: entityId, patch: mergedData });
  }

  async deleteEntity(entityId: string): Promise<void> {
    // Determine entity type by fetching the record first
    const existing = await this.getEntity(entityId);
    if (!existing) throw new Error(`Entity not found: ${entityId}`);
    await this.remove({ id: entityId });
  }

  async createBulkEntities(
    entities: CreateBulkEntityRequest[],
    actorId?: string,
  ): Promise<Entity[]> {
    if (!entities.length) {
      return [];
    }

    const rpcEndpoint = this.baseEndpoint.replace('/entities', '/rpc/create_entities_bulk');
    const payload: Record<string, unknown> = {
      p_entities: entities.map((entity) => ({
        ...entity,
        status: entity.status ?? 'active',
      })),
    };

    if (actorId) {
      payload.p_actor = actorId;
    }

    const response = await httpClient.post<Entity[] | Entity>(rpcEndpoint, payload);
    const created = Array.isArray(response) ? response : [response];

    if (created.length !== entities.length) {
      log.warn('Bulk create returned unexpected row count', {
        requested: entities.length,
        received: created.length,
      });
    }

    return created;
  }

  async searchEntities(query: string, entityType?: string): Promise<Entity[]> {
    const searchParams = new URLSearchParams();
    if (entityType) searchParams.set('entity_type', `eq.${entityType}`);
    searchParams.set('is_current', 'eq.true');
    searchParams.set('order', 'display_name.asc');
    const q = query.trim();
    searchParams.set('or', buildPostgrestOrIlikeFilter(['display_name', 'entity_identifier'], q));
    const endpoint = `${this.baseEndpoint}?${searchParams}`;
    return await httpClient.get<Entity[]>(endpoint);
  }

  async searchStockCatalogs(query: string): Promise<Entity[]> {
    const searchParams = new URLSearchParams();
    searchParams.set('entity_type', 'eq.catalog');
    searchParams.set('is_current', 'eq.true');
    searchParams.set('entity_data->>type', 'eq.stock');
    searchParams.set('order', 'display_name.asc');
    searchParams.set('limit', '200');

    const q = query.trim();
    if (q) {
      searchParams.set(
        'or',
        buildPostgrestOrIlikeFilter(
          ['display_name', 'entity_identifier', 'entity_data->>nsn', 'entity_data->>lin'],
          q,
        ),
      );
    }

    const endpoint = `${this.baseEndpoint}?${searchParams}`;
    return await httpClient.get<Entity[]>(endpoint);
  }
}

// Singleton instance - used throughout the application
export const entityService = new EntityService();
